import React, { useState, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Tabs, Tab, Box } from "@mui/material";
import TicketingTab from "./TicketingTab/TicketingTab";
import RegistrationTab from "./RegistrationTab/RegistrationTab";
// import WithdrawalTab from "./WithdrawalTab/Withdrawal"; // ← Commented out
import { Calendar, Users, CreditCard, Sparkles, CheckCircle } from "lucide-react";

// Custom theme with professional styling
const theme = createTheme({
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: "none",
          fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
          padding: { xs: "10px 12px", sm: "14px 20px", md: "16px 24px" },
          minWidth: { xs: "auto", sm: "140px" },
          borderRadius: 10,
          margin: { xs: "0 2px", sm: "0 6px" },
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&.Mui-selected": {
            color: "#F68A20",
            backgroundColor: "rgba(246, 138, 32, 0.12)",
            boxShadow: "0 4px 12px rgba(246, 138, 32, 0.2)",
          },
          "&:not(.Mui-selected)": {
            color: "#6b7280",
            "&:hover": {
              color: "#F68A20",
              backgroundColor: "rgba(246, 138, 32, 0.06)",
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: { xs: "48px", sm: "56px" },
          "& .MuiTabs-indicator": {
            display: "none",
          },
        },
        flexContainer: {
          justifyContent: { xs: "flex-start", sm: "center" },
          gap: { xs: "4px", sm: "8px" },
        },
        scrollButtons: {
          color: "#F68A20",
        },
      },
    },
  },
  palette: {
    secondary: {
      main: "#F68A20",
      light: "#fb923c",
      dark: "#ea580c",
    },
  },
});

const formatLabel = (label) =>
  `${label.charAt(0).toUpperCase()}${label.slice(1).toLowerCase()}`;

const tabIcons = {
  "1": <Users size={16} />,
  "2": <CreditCard size={16} />,
  // "3": <Calendar size={16} />, // ← Commented out (Withdrawal icon)
};

const Registration = ({ userID, token, eventDetails }) => {
  const [selectedTab, setSelectedTab] = useState("1");
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const isMobile = windowWidth < 768;
  
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const eventName = eventDetails?.name || eventDetails?.title || "Event";

  return (
    <ThemeProvider theme={theme}>
      <div className="set-event-rightbar-content" style={{
        minHeight: '100vh',
        background: isMobile 
          ? 'linear-gradient(180deg, #fff9f5 0%, #ffffff 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        position: 'relative',
        overflowX: 'hidden',
      }}>
        {/* Animated Background Pattern */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '300px',
          background: isMobile 
            ? 'linear-gradient(135deg, rgba(246,138,32,0.08) 0%, rgba(251,146,60,0.04) 100%)'
            : 'radial-gradient(circle at 50% 0%, rgba(246,138,32,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0,
        }} />

        {/* ===== MODERN STICKY HEADER ===== */}
        <Box
          sx={{
            backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
            width: "100%",
            position: "sticky",
            top: 0,
            zIndex: 100,
            boxShadow: isScrolled 
              ? "0 4px 20px rgba(246, 138, 32, 0.12)" 
              : "0 2px 10px rgba(0,0,0,0.04)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            backdropFilter: "blur(12px)",
            borderBottom: isScrolled ? "1px solid rgba(246, 138, 32, 0.1)" : "none",
          }}
          className="set-event-rightbar-content-header"
        >
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: isMobile ? '12px 16px' : '16px 32px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: isMobile ? '12px' : '0',
          }}>
            {/* Left: Event Info */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12,
              width: isMobile ? '100%' : 'auto',
            }}>
              <div style={{
                width: 44,
                height: 44,
                background: 'linear-gradient(135deg, #F68A20 0%, #fb923c 100%)',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 15px rgba(246, 138, 32, 0.35)',
                transform: 'rotate(-3deg)',
                transition: 'transform 0.3s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate(0deg)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate(-3deg)'}
              >
                <Sparkles size={22} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  margin: 0,
                  fontSize: isMobile ? '1.15rem' : '1.25rem',
                  fontWeight: 800,
                  color: '#1f2937',
                  letterSpacing: '-0.02em',
                }}>
                  {eventName}
                </h3>
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '0.85rem',
                  color: '#F68A20',
                  fontWeight: 500,
                }}>
                  Registration Setup
                </p>
              </div>
            </div>

            {/* Center: Modern Tab Navigation */}
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              textColor="secondary"
              aria-label="registration navigation tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                flex: isMobile ? 1 : 'auto',
                width: isMobile ? '100%' : 'auto',
                maxWidth: { xs: '100%', sm: '600px' },
                margin: { xs: '0', sm: '0 32px' },
                '& .MuiTabs-flexContainer': {
                  justifyContent: isMobile ? 'flex-start' : 'center',
                },
              }}
            >
              {[
                { value: "1", label: "Registration", icon: tabIcons["1"] },
                ...(eventDetails?.ticketsSold === true 
                  ? [{ value: "2", label: "Ticketing", icon: tabIcons["2"] }] 
                  : []),
                // { value: "3", label: "Withdrawal", icon: tabIcons["3"] }, // ← Commented out
              ].map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: isMobile ? '0.85rem' : '0.95rem',
                    }}>
                      {tab.icon}
                      <span style={{ 
                        fontWeight: selectedTab === tab.value ? 700 : 600,
                        whiteSpace: 'nowrap',
                      }}>
                        {tab.label}
                      </span>
                    </span>
                  }
                />
              ))}
            </Tabs>

            {/* Right: Status Indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)',
              borderRadius: 20,
              border: '1px solid rgba(16, 185, 129, 0.25)',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)',
            }}>
              <div style={{
                width: 8,
                height: 8,
                background: '#10b981',
                borderRadius: '50%',
                animation: 'pulse 2s infinite',
                boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.2)',
              }} />
              <span style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#10b981',
              }}>
                Active
              </span>
            </div>
          </div>
        </Box>

        {/* ===== TAB CONTENT ===== */}
        <Box
          sx={{
            padding: { xs: "20px 16px", sm: "32px 24px", md: "40px 32px" },
            paddingTop: { xs: "24px", sm: "32px" },
            maxWidth: '1400px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Section Header */}
          <div style={{
            marginBottom: isMobile ? '28px' : '32px',
            textAlign: 'center',
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '12px',
              padding: '6px 16px',
              background: 'linear-gradient(135deg, rgba(246,138,32,0.12) 0%, rgba(251,146,60,0.08) 100%)',
              borderRadius: '20px',
              border: '1px solid rgba(246,138,32,0.2)',
            }}>
              <CheckCircle size={16} color="#F68A20" />
              <span style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#F68A20',
              }}>
                {/* Updated step logic - max 2 tabs now */}
                Step {selectedTab} of {eventDetails?.ticketsSold === true ? "2" : "1"}
              </span>
            </div>
            
            <h2 style={{
              fontSize: isMobile ? '1.85rem' : '2.25rem',
              fontWeight: 800,
              color: '#1f2937',
              marginBottom: '12px',
              background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
            }}>
              {selectedTab === "1" && "Registration Form Setup"}
              {selectedTab === "2" && "Ticketing Configuration"}
              {/* {selectedTab === "3" && "Withdrawal Settings"} */}
            </h2>
            
            <p style={{
              fontSize: isMobile ? '0.95rem' : '1.125rem',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: 1.6,
            }}>
              {selectedTab === "1" && "Configure your registration form fields, questions, and attendee information collection."}
              {selectedTab === "2" && "Manage ticket types, pricing, availability, and sales settings for your event."}
              {/* {selectedTab === "3" && "Set up withdrawal methods, schedules, and financial tracking for event revenue."} */}
            </p>

            {/* Progress Bar - Updated for 2 steps max */}
            <div style={{
              maxWidth: '400px',
              margin: '24px auto 0',
              padding: '0 20px',
            }}>
              <div style={{
                display: 'flex',
                gap: '8px',
                height: '6px',
              }}>
                {[1, 2].map((step) => ( // ← Only 2 steps now
                  <div
                    key={step}
                    style={{
                      flex: 1,
                      background: step <= parseInt(selectedTab) 
                        ? 'linear-gradient(90deg, #F68A20 0%, #fb923c 100%)'
                        : '#e5e7eb',
                      borderRadius: '3px',
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Tab Content Cards */}
          <div style={{
            background: 'white',
            borderRadius: isMobile ? '20px' : '24px',
            boxShadow: isMobile 
              ? '0 8px 30px rgba(246, 138, 32, 0.12)'
              : '0 8px 30px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(246, 138, 32, 0.12)',
            overflow: 'hidden',
            position: 'relative',
          }}>
            {/* Decorative corner accent */}
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: 'linear-gradient(135deg, rgba(246,138,32,0.05) 0%, transparent 100%)',
              pointerEvents: 'none',
            }} />
            
            {selectedTab === "1" && (
              <RegistrationTab
                userID={userID}
                token={token}
                eventID={eventDetails?._id}
                eventDetails={eventDetails}
              />
            )}
            {selectedTab === "2" && (
              <TicketingTab
                userID={userID}
                token={token}
                eventID={eventDetails?._id}
                eventDetails={eventDetails}
              />
            )}
            {/* {selectedTab === "3" && (
              <WithdrawalTab
                userID={userID}
                token={token}
                eventID={eventDetails?._id}
              />
            )} */}
          </div>
        </Box>
      </div>

      {/* ===== GLOBAL STYLES & ANIMATIONS ===== */}
      <style>{`
        @keyframes pulse {
          0%, 100% { 
            opacity: 1; 
            transform: scale(1);
            box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
          }
          50% { 
            opacity: 0.7; 
            transform: scale(1.1);
            box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.1);
          }
        }
        
        @media (max-width: 768px) {
          .set-event-rightbar-content {
            padding: 0 !important;
          }
          
          .set-event-rightbar-content-header {
            border-radius: 0 !important;
          }
          
          /* Improve touch targets on mobile */
          .MuiTab-root {
            min-height: 44px !important;
          }
          
          /* Mobile form optimizations */
          input[type="text"],
          input[type="email"],
          input[type="tel"],
          input[type="password"],
          input[type="number"],
          textarea,
          select {
            width: 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            padding: 14px 16px !important;
            font-size: 16px !important;
            border-radius: 12px !important;
            border: 2px solid #e5e7eb !important;
            transition: all 0.3s ease !important;
          }
          
          input:focus,
          textarea:focus,
          select:focus {
            border-color: #F68A20 !important;
            box-shadow: 0 0 0 4px rgba(246, 138, 32, 0.1) !important;
            outline: none !important;
          }
          
          .MuiFormControl-root,
          [class*="form-control"],
          div[style*="margin-bottom"] {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 0 16px 0 !important;
            box-sizing: border-box !important;
          }
          
          label,
          .MuiFormLabel-root {
            font-size: 14px !important;
            font-weight: 600 !important;
            margin-bottom: 8px !important;
            display: block !important;
            width: 100% !important;
            color: #374151 !important;
          }
          
          .MuiPaper-root,
          [class*="form-section"],
          div[style*="background: white"] {
            padding: 20px !important;
            margin: 0 !important;
            border-radius: 16px !important;
          }
          
          .MuiGrid-container,
          [class*="grid-container"] {
            flex-direction: column !important;
            width: 100% !important;
          }
          
          .MuiGrid-item,
          [class*="grid-item"] {
            width: 100% !important;
            max-width: 100% !important;
            flex: 0 0 100% !important;
          }
          
          button[style*="Edit Form"],
          .edit-form-btn {
            width: 100% !important;
            max-width: 100% !important;
            padding: 16px 24px !important;
            font-size: 15px !important;
            font-weight: 600 !important;
            border-radius: 12px !important;
          }
        }
        
        html {
          scroll-behavior: smooth;
        }
        
        body,
        html,
        #root {
          overflow-x: hidden !important;
          max-width: 100vw !important;
        }
      `}</style>
    </ThemeProvider>
  );
};

export default Registration;